import React, { useState } from "react";
import { Draggable } from "@hello-pangea/dnd";
import OrderDetails from "./OrderDetails";
import axios from "axios";
import Modal from "./Modal";

export default function OrderCard({
  order,
  index,
  expanded,
  onToggle,
  setIsAnyModalActive,
  refetchOrders,
}) {
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const openCancelModal = (e) => {
    e.stopPropagation();
    setShowCancelModal(true);
    setIsAnyModalActive?.(true);
  };

  const closeCancelModal = () => {
    if (loading) return;
    setShowCancelModal(false);
    setIsAnyModalActive?.(false);
  };

  // ✅ Intl digits only
  const formatWhatsAppPhone = (phone) => {
    if (!phone) return "";
    return String(phone).replace(/\D/g, "");
  };

  // ✅ Whatsapp icon in card (just opens chat)
  const handleWhatsAppCustomer = (e) => {
    e.stopPropagation();

    const phone = formatWhatsAppPhone(order.customer_phone);
    if (!phone) {
      alert("Customer phone number not available.");
      return;
    }

    window.open(`https://wa.me/${phone}`, "_blank", "noopener,noreferrer");
  };

  // ✅ One click: open WhatsApp + cancel order
  const handleNotifyAndCancel = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (loading) return;

    const phone = formatWhatsAppPhone(order.customer_phone);
    if (!phone) {
      alert("Customer phone number not available.");
      return;
    }

    const customerName = order.customer_name?.trim() || "there";
    const orderNo = order.order_number?.trim()
      ? `#${order.order_number}`
      : `Ref ${order.id}`;

    const message = `Hi ${customerName}, your order ${orderNo} has been cancelled and will not be processed. Sorry for any inconvenience.`;

    // 1) Open WhatsApp first (so popup blockers are less likely to block it)
    window.open(
      `https://wa.me/${phone}?text=${encodeURIComponent(message)}`,
      "_blank",
      "noopener,noreferrer"
    );

    // 2) Cancel immediately
    setLoading(true);
    try {
      await axios.post("https://admin.deedelicious.com/php/cancel-order.php", {
        order_id: order.id,
      });

      if (refetchOrders) {
        await refetchOrders();
      }

      closeCancelModal();
    } catch (err) {
      console.error("Failed to Cancel Order:", err);
      alert("Failed to Cancel Order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Draggable draggableId={order.id.toString()} index={index}>
        {(provided) => (
          <li
            className={`order-card ${expanded ? "expanded" : ""}`}
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            onClick={onToggle}
          >
            <div className="order-date">
              <span className="order-date-text">
                {new Date(order.created_at).toLocaleDateString("en-GB")}
              </span>
              <span className="order-id">Ref: {order.id}</span>
            </div>

            <div className="order-header">
              <div className="order-header-left">
                <div className="order-header-top">
                  <p>
                    <strong>Order #{order.order_number}</strong>
                  </p>

                  {order.order_type === "Eat In" && order.table_number && (
                    <p>
                      <strong>Table: </strong> {order.table_number}
                    </p>
                  )}

                  <p>
                    <strong>{order.order_type}</strong>
                  </p>
                </div>

                <div className="order-header-bottom">
                  <p>
                    <strong>Customer Name: </strong>
                    {order.customer_name || "Unnamed Customer"}
                  </p>
                  <p className="order-total">
                    <strong>£{Number(order.total_price || 0).toFixed(2)}</strong>
                  </p>
                </div>
              </div>

              {/* Action Icons */}
              <div className="order-header-right">
                <i
                  className="fa-brands fa-whatsapp whatsapp-icon"
                  onClick={handleWhatsAppCustomer}
                  title="Message customer on WhatsApp"
                />

                <i
                  className="fa-regular fa-circle-xmark cancel-order-icon"
                  onClick={openCancelModal}
                  title="Cancel Order"
                />
              </div>
            </div>

            {expanded && <OrderDetails order={order} />}
          </li>
        )}
      </Draggable>

      {/* Cancel Modal */}
      {showCancelModal && (
        <Modal
          title="Cancel Order"
          onClose={closeCancelModal}
          showCancel={false}
          showSubmitButton={false}
          className="cancel-order-modal"
        >
          <div className="delete-modal-heading">
            <p>
              This will notify the customer on WhatsApp and cancel the order.
              <br />
              Customer phone:{" "}
              <strong>{formatWhatsAppPhone(order.customer_phone) || "N/A"}</strong>
            </p>
          </div>

          <div style={{ marginTop: "16px" }}>
            <button
              type="button"
              onClick={handleNotifyAndCancel}
              className="profiles-btn add-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin" /> Cancelling…
                </>
              ) : (
                "Notify & Cancel"
              )}
            </button>
          </div>

          <p style={{ fontSize: "13px", marginTop: "8px", color: "#888" }}>
            Note: WhatsApp may open in a new tab.
          </p>
        </Modal>
      )}
    </>
  );
}
